import equipmentStatusUtils from '@/pages/Equipment/components/Common/EquipmentStatus/equipmentStatus.utils'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import KeyValueTable from '@/shared/Tables/KeyValueTable'
import css from './liftCardWidget.module.css'
import { IEquipment } from '@/pages/Equipment/equipment.types'
import ErrorIcon from '@/assets/icons/alerts/error.svg?react'
import WarningIcon from '@/assets/icons/alerts/warning.svg?react'
import ToolsIcon from '@/assets/icons/tools.svg?react'
import RedoOutlinedIcon from '@/assets/icons/redo-outlined.svg?react'
import { ReactNode } from 'react'
import { IParameter, TParameterKey } from '@/pages/Parameters/parameters.types'
import Tag from '@/shared/Tags/Tag'
import { ILiftCardLabels, ILiftCardParameters } from '../liftCard.config'
import ProtectedContent from '@/services/rights/ProtectedContent'
import Button from '@/shared/Buttons/Button'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import equipmentApi from '@/pages/Equipment/equipment.api'
import equipmentEvents from '@/pages/Equipment/events/equipment/equipment.events'

interface IProps {
  chart: ReactNode
  equipment: IEquipment
  live?: boolean
  loading: boolean
  values: Record<TParameterKey, number>
  labels: ILiftCardLabels
  parameters: ILiftCardParameters<IParameter['id']>
}

const LiftCardWidget = (props: IProps) => {
  const { loading: resetStatusFlagLoading, wait: waitResetStatusFlag } = useHttpLoader()

  const handleResetStatusFlag = () => {
    waitResetStatusFlag(equipmentApi.resetEquipmentStatusFlag(props.equipment.id), (resp) => {
      if (resp.status === 'success') {
        equipmentEvents.updated(resp.body)
      }
    })
  }

  const renderParameters = () => {
    if (props.live !== false && props.equipment.state !== 'RUNNING') {
      return (
        <div className={css.emptyWrapper}>
          <EmptyPlaceholder inscription="Отсутствуют параметры для отображения" />
        </div>
      )
    }

    if (props.loading) {
      return (
        <div className={css.emptyWrapper}>
          <ThreeDotsLoader inscription="Загрузка..." />
        </div>
      )
    }

    const speedWholeValue = props.values[props.parameters.speedWhole] ?? 0
    const speedFractionalValue = props.parameters.speedFractional
      ? (props.values[props.parameters.speedFractional] ?? 0)
      : 0
    const isSpeedNumeric = Boolean(props.parameters.speedFractional)
    const isLiftMoving = isSpeedNumeric ? speedWholeValue > 0 || speedFractionalValue > 0 : Boolean(speedWholeValue)
    const speedValue = isSpeedNumeric
      ? Number(`${speedWholeValue}.${speedFractionalValue.toString().padStart(2, '0')}`).toFixed(2)
      : null
    const destinationValue = props.values[props.parameters.destinationLevel]
    const hasDestinationValue = destinationValue !== undefined && destinationValue !== null

    return (
      <KeyValueTable
        values={[
          {
            key: 'speed',
            title: <span className="text-nm">{props.labels.speed}</span>,
            value: isLiftMoving ? (
              <Tag color="green">
                <span className="text-caption color-inherit">{isSpeedNumeric ? `${speedValue} м/с` : 'Движется'}</span>
              </Tag>
            ) : (
              <Tag color="gray">
                <span className="text-caption color-inherit">Не движется</span>
              </Tag>
            ),
          },
          {
            key: 'destinationFloor',
            title: <span className="text-nm">{props.labels.destination}</span>,
            value: hasDestinationValue ? (
              <Tag color="green">
                <span className="text-caption color-inherit">{destinationValue}</span>
              </Tag>
            ) : (
              <Tag color="gray">
                <span className="text-caption color-inherit">Нет</span>
              </Tag>
            ),
          },
          {
            key: 'ksmContactorSignal',
            title: <span className="text-nm">{props.labels.ksmContactor}</span>,
            value: (
              <Tag color={props.values[props.parameters.ksmContactor] ? 'green' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.ksmContactor] ? 'Вкл' : 'Выкл'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'safetyChainSignal',
            title: <span className="text-nm">Цепь безопасности</span>,
            value: (
              <Tag color={props.values[props.parameters.safetyChain] ? 'green' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.safetyChain] ? 'Ок' : 'Не собрана'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'kdsReleState',
            title: <span className="text-nm">{props.labels.kdsRelay}</span>,
            value: (
              <Tag color={props.values[props.parameters.kdsRelay] ? 'green' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.kdsRelay] ? 'Вкл' : 'Выкл'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'K7ReleState',
            title: <span className="text-nm">{props.labels.k7Relay}</span>,
            value: (
              <Tag color={props.values[props.parameters.k7Relay] ? 'green' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.k7Relay] ? 'Вкл' : 'Выкл'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'weight15kg',
            title: <span className="text-nm">Сигнал 15кг</span>,
            value: (
              <Tag color={props.values[props.parameters.weight15] ? 'green' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.weight15] ? 'Да' : 'Нет'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'weight90percent',
            title: <span className="text-nm">Сигнал грузовеса 90%</span>,
            value: (
              <Tag color={props.values[props.parameters.weight90] ? 'orange' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.weight90] ? 'Да' : 'Нет'}
                </span>
              </Tag>
            ),
          },
          {
            key: 'weight110percent',
            title: <span className="text-nm">Сигнал грузовеса 110%</span>,
            value: (
              <Tag color={props.values[props.parameters.weight110] ? 'red' : 'gray'}>
                <span className="text-caption color-inherit">
                  {props.values[props.parameters.weight110] ? 'Да' : 'Нет'}
                </span>
              </Tag>
            ),
          },
        ]}
      />
    )
  }

  const renderLiftError = () => {
    if (props.equipment.statusFlag === 'NORMAL') {
      return null
    }

    const resetButton = (
      <ProtectedContent adminOnly={true}>
        <Button
          size="default"
          type="default"
          className={css.resetStatusFlagButton}
          icon={<RedoOutlinedIcon style={{ width: '16px', height: '16px' }} />}
          onClick={handleResetStatusFlag}
          loading={resetStatusFlagLoading}
        />
      </ProtectedContent>
    )

    if (props.equipment.statusFlag === 'IN_MAINTENANCE') {
      return (
        <div className={css.errorCard} data-maintenance="true">
          <ToolsIcon style={{ flexShrink: 0, color: '#2f54eb' }} />
          <p className="text-lg" style={{ color: '#2f54eb' }}>
            В обслуживании
          </p>
          {resetButton}
        </div>
      )
    }

    let text = ''
    if (props.equipment.errorCode) {
      text = `${props.equipment.errorCode}: ${props.equipment.errorDescription}`
    } else {
      text = equipmentStatusUtils.getLiftStatusFlagText(props.equipment.statusFlag)
    }

    return (
      <div
        className={css.errorCard}
        data-warning={props.equipment.statusFlag === 'WARNING'}
        data-error={props.equipment.statusFlag !== 'WARNING'}
      >
        {props.equipment.statusFlag === 'WARNING' ? (
          <WarningIcon style={{ flexShrink: 0 }} />
        ) : (
          <ErrorIcon style={{ flexShrink: 0 }} />
        )}
        <p className="text-lg">{text}</p>
        {resetButton}
      </div>
    )
  }

  return (
    <div className={css.wrapper}>
      <div className={css.svgWrapper}>
        {props.chart}
        {props.live !== false && renderLiftError()}
      </div>
      <div className={css.tableWrapper}>{renderParameters()}</div>
    </div>
  )
}

export default LiftCardWidget
